import { scrollToTarget } from '@/lib/smooth-scroll';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowDown, ArrowUpRight, Play, Check, Sparkles, Layers, FileText } from 'lucide-react';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { Button } from '@/components/ui/Button';
import { createHeroSequence } from './hero-sequence';
import './hero-cinema.css';

const outline = 'M150 28C217 28 272 83 272 150C272 217 217 272 150 272C83 272 28 217 28 150C28 83 83 28 150 28Z';
const bloom = 'M150 16C188 74 227 25 247 80C268 132 213 131 282 150C223 177 271 222 219 246C168 269 176 214 150 284C118 221 79 271 54 217C31 165 89 178 16 150C78 119 27 75 81 53C131 31 119 84 150 16Z';
const resolved = 'M150 25C218 25 275 81 275 150C275 218 218 275 150 275C81 275 25 218 25 150C25 81 81 25 150 25Z';

export function HeroCinemaPortal() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const route = useRef<SVGPathElement>(null);

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add({ motion: '(prefers-reduced-motion: no-preference)', desktop: '(min-width: 900px) and (min-height: 700px)' }, (context) => {
      if (!context.conditions?.motion) return;
      const desktop = !!context.conditions.desktop;
      // A finite entrance leaves the message and actions still and readable.
      const entrance = gsap.timeline({ defaults: { ease: 'power3.out' } });
      entrance.from('.hc-enter', { y: 24, opacity: 0, duration: 0.9, stagger: 0.1 }, 0.05)
        .from('.hc-stage-intro', { y: 36, opacity: 0, duration: 1.2 }, 0.3);

      const images = createHeroSequence();
      const playhead = { frame: 0 };
      const ctx = canvas.current?.getContext('2d');
      let lastFrame = -1;
      const paint = () => {
        const index = Math.round(playhead.frame);
        if (!ctx || index === lastFrame) return;
        lastFrame = index;
        ctx.clearRect(0, 0, 480, 280);
        ctx.drawImage(images[index], 0, 0);
      };
      paint();
      const film = gsap.timeline({
        defaults: { ease: 'power2.inOut' },
        scrollTrigger: {
          trigger: stage.current,
          start: desktop ? 'top 88px' : 'top 75%',
          end: desktop ? '+=1100' : 'bottom 25%',
          pin: desktop, pinSpacing: true, scrub: 0.8,
          anticipatePin: 1, invalidateOnRefresh: true,
        },
      });
      film
        .fromTo('.hc-portal', { clipPath: 'inset(4% 5% 4% 5% round 32px)' }, { clipPath: 'inset(0% 0% 0% 0% round 16px)', duration: 2.4 }, 0)
        .fromTo('.hc-camera', { rotationX: desktop ? 12 : 4, rotationY: desktop ? -14 : -5, rotationZ: -2, scale: 0.88, y: 25 }, { rotationX: 0, rotationY: 0, rotationZ: 0, scale: 1, y: 0, duration: 2.5 }, 0)
        .fromTo('.hc-blueprint', { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 1.3, stagger: 0.08 }, 0)
        .fromTo(route.current, { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 2.7 }, 0)
        .fromTo('.hc-spark', { opacity: 0 }, { opacity: 1, duration: 0.2 }, 0)
        .to('.hc-spark', { motionPath: { path: route.current!, align: route.current!, alignOrigin: [0.5, 0.5] }, duration: 2.7, ease: 'none' }, 0)
        .fromTo('.hc-art-shape', { morphSVG: outline }, { morphSVG: bloom, duration: 2 }, 0.2)
        .fromTo('.hc-slide-ink', { clipPath: 'circle(0% at 75% 50%)' }, { clipPath: 'circle(150% at 75% 50%)', duration: 2.2 }, 0.35)
        .fromTo('.hc-slide-detail', { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.1 }, 1.3)
        .to('.hc-note', { y: -20, opacity: 0, duration: 0.8 }, 1.6)
        .to('.hc-camera', { rotationY: desktop ? 9 : 3, rotationX: desktop ? -5 : 0, scale: 0.96, duration: 1.6 }, 2.6)
        .to('.hc-layer-back', { x: desktop ? -48 : -12, y: -24, rotationZ: -5, duration: 1.6 }, 2.6)
        .to('.hc-layer-mid', { x: desktop ? 38 : 12, y: 24, rotationZ: 4, duration: 1.6 }, 2.6)
        .to('.hc-art-shape', { morphSVG: resolved, rotation: 90, transformOrigin: '50% 50%', duration: 1.6 }, 2.6)
        .to('.hc-spark', { opacity: 0, duration: 0.4 }, 2.6)
        .to('.hc-camera', { rotationY: 0, rotationX: 0, scale: 1, duration: 1.1 }, 4.2)
        .to('.hc-layer-back, .hc-layer-mid', { x: 0, y: 0, rotationZ: 0, duration: 1.1 }, 4.2)
        .fromTo('.hc-ready', { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.8 }, 4.3)
        .to(playhead, { frame: images.length - 1, duration: 5.4, ease: 'none', onUpdate: paint }, 0)
        .fromTo('.hc-film-progress', { scaleX: 0 }, { scaleX: 1, duration: 5.4, ease: 'none' }, 0);

      const onVisibility = () => {
        entrance.paused(document.hidden);
        if (document.hidden) {
          film.scrollTrigger?.getTween()?.pause();
          film.scrollTrigger?.disable(false);
        } else film.scrollTrigger?.enable(false);
      };
      document.addEventListener('visibilitychange', onVisibility);
      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        images.forEach((image) => { image.width = 0; image.height = 0; });
        ctx?.clearRect(0, 0, 480, 280);
      };
    });
    return () => media.revert();
  }, { scope: root, dependencies: [i18n.resolvedLanguage], revertOnUpdate: true });

  const explore = (id: string) => {
    const target = document.getElementById(id);
    if (!target) return;
    scrollToTarget(target, { offset: -88 });
    target.focus({ preventScroll: true });
  };

  return (
    <div ref={root} className="hero-cinema">
      <section className="hc-intro" aria-labelledby="hero-title">
        <div className="hc-ambient" aria-hidden="true" />
        <h1 id="hero-title" className="hc-enter focus:outline-none" tabIndex={0}>
          {t('hero.title')}
          <span>{t('hero.titleAccent')}</span>
        </h1>
        <p className="hc-description hc-enter">{t('hero.subtitle')}</p>
        <div className="hc-actions hc-enter">
          <Button size="lg" onClick={() => navigate('/coming-soon')} className="hc-primary">{t('hero.ctaPrimary')}<ArrowRight size={16} className="ml-3" aria-hidden="true" /></Button>
          <Button size="lg" variant="ghost" onClick={() => explore('hero-preview')}><Play size={13} className="mr-2" aria-hidden="true" />{t('hero.ctaSecondary')}</Button>
        </div>
      </section>
      <section ref={stage} id="hero-preview" tabIndex={-1} className="hc-stage" aria-label={t('hero.previewLabel')}>
        <div className="hc-stage-intro">
          <div className="hc-portal">
            <div className="hc-stage-top"><span><Layers size={13} aria-hidden="true" />{t('hero.studioLabel')}</span><span className="hc-stage-meta">01 / 03 <span>—</span> {t('hero.sampleLabel')}</span></div>
            <canvas ref={canvas} width={480} height={280} className="hc-sequence" aria-hidden="true" />
            <svg className="hc-trajectory" viewBox="0 0 1200 620" fill="none" aria-hidden="true"><path ref={route} d="M60 430C-20 180 280 28 610 65C950 100 1260 270 1120 445C980 600 430 585 170 480" stroke="currentColor" strokeWidth="1" /><g className="hc-spark"><circle r="10" fill="currentColor" opacity=".12" /><circle r="3" fill="currentColor" /></g></svg>
            <div className="hc-camera">
              <div className="hc-layer hc-layer-back" aria-hidden="true"><span>03 / {t('hero.layerNext')}</span></div>
              <div className="hc-layer hc-layer-mid" aria-hidden="true"><span>02 / {t('hero.layerStory')}</span></div>
              <div className="hc-slide">
                <svg className="hc-wireframe" viewBox="0 0 800 450" fill="none" aria-hidden="true"><path className="hc-blueprint" d="M28 28H772V422H28Z" /><path className="hc-blueprint" d="M55 108H370V245H55Z" /><path className="hc-blueprint" d="M435 65H735V365H435Z" /><path className="hc-blueprint" d="M55 290H350M55 310H290M55 365H350" /></svg>
                <div className="hc-slide-ink">
                  <div className="hc-deck-top"><span>NOVA / {t('hero.deckCategory')}</span><span>STRATEGY — 2026</span></div>
                  <div className="hc-deck-body"><div className="hc-deck-copy"><span className="hc-deck-kicker">{t('hero.deckEyebrow')}</span><h2>{t('hero.deckTitle')}<em>{t('hero.deckAccent')}</em></h2><p className="hc-slide-detail">{t('hero.deckDescription')}</p><div className="hc-deck-stat hc-slide-detail"><strong>3.2<span>×</span></strong><span>{t('hero.deckMetric')}</span><ArrowUpRight size={20} aria-hidden="true" /></div></div>
                    <div className="hc-art" aria-hidden="true"><svg viewBox="0 0 300 300"><defs><linearGradient id="hc-sculpture" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#e5f1b0" /><stop offset=".45" stopColor="#7ba893" /><stop offset="1" stopColor="#1a5051" /></linearGradient></defs><path className="hc-art-shape" d={bloom} fill="url(#hc-sculpture)" /><ellipse cx="150" cy="150" rx="112" ry="44" fill="none" stroke="#e6f5ca" strokeWidth=".7" transform="rotate(-35 150 150)" /><ellipse cx="150" cy="150" rx="85" ry="125" fill="none" stroke="#e6f5ca" strokeWidth=".7" transform="rotate(35 150 150)" /><circle cx="150" cy="150" r="46" fill="#133c3d" /><circle cx="150" cy="150" r="31" fill="#dce8b9" /><circle cx="150" cy="150" r="17" fill="#285952" /></svg><span>{t('hero.artCaption')}</span></div>
                  </div>
                  <div className="hc-deck-bottom"><span>{t('hero.deckFooter')}</span><span>01 — 12</span></div>
                </div>
              </div>
            </div>
            <div className="hc-note"><span className="hc-note-icon"><FileText size={16} aria-hidden="true" /></span><div><span>{t('hero.inputLabel')}</span><p>{t('hero.inputPrompt')}</p></div><Sparkles size={15} aria-hidden="true" /></div>
            <div className="hc-ready"><span><Check size={13} aria-hidden="true" /></span><div><strong>{t('hero.readyTitle')}</strong><p>{t('hero.readyDescription')}</p></div></div>
            <div className="hc-stage-bottom"><span>{t('hero.scrollCue')}<ArrowDown size={12} aria-hidden="true" /></span><span>{t('hero.outputLabel')}</span></div>
            <div className="hc-film-progress" aria-hidden="true" />
          </div>
        </div>
      </section>
    </div>
  );
}

export default HeroCinemaPortal;
